import React from "react";
import { useNavigate } from "react-router-dom";

import { computeBodyLoadPct } from "../../utils/fleetUtils";

import "./fleetCard.css";
import FleetCardStatus from "../../pages/fleetmap/FleetCardStatus";
import iconEmployee from "../../assets/icon_employee.jpg";

const STATUS_ICONS = {
  online: "bi-bolt-fill",
  idle: "bi-pause-fill",
  offline: "bi-moon-fill",
};

const VALID_STATUSES = new Set(["online", "idle", "offline"]);
const VALID_SUBSCRIPTIONS = new Set(["ACTIVE", "TRIAL"]);

const toNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

// const formatWeight = (kg) => (kg ? `${(kg / 1000).toFixed(1)}t` : "0t");
const formatWeight = (kg) => {
  return `${(toNumber(kg) / 1000).toFixed(1)}t`;
};

const FleetCard = React.memo(
  ({ index, fleet, onSelect, isSelected, onTrack, isTracking }) => {
    //guard
    if (!fleet) {
      return null;
    }

    if (import.meta.env.DEV) {
      console.log("debug ujj FleetCard", {
        vehicleId: fleet.vehicleId,
        detailsKeys: Object.keys(fleet || {}),
        fleet: fleet,
      });
    }

    const navigate = useNavigate();

    const speedKmh = toNumber(fleet.speedKmh);
    const employeeImg = fleet.employee?.imageUrl || iconEmployee;
    const employeeName = fleet.employee?.name || "UNASSIGNED_DRIVER";
    const source = fleet.source || "haulweigh";
    const hasAlert = fleet.hasAlert;

    const vehicleCode =
      fleet.vehicleCode ||
      `VEH-${String(fleet.vehicleId ?? "").slice(-4) || "----"}`;

    const status = VALID_STATUSES.has(fleet.status) ? fleet.status : "offline";
    const statusIcon = STATUS_ICONS[status];

    const bodyLoadPct = computeBodyLoadPct(fleet.weights, fleet.weightsMax);

    const hasValidSubscription = VALID_SUBSCRIPTIONS.has(
      fleet.subscriptionStatus,
    );
    
    const handleSelect = () => {
      onSelect?.(fleet.vehicleId);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleSelect();
      }
    };

    return (
      <div
        className={`fleet-card ${isSelected ? "active" : ""}`}
        style={{ animationDelay: `${Math.min(index, 10) * 0.1}s` }}
        onClick={handleSelect}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-pressed={Boolean(isSelected)}
      >
        <div className="fleet-card-header">
          <div className="fc-img-container">
            <img src={employeeImg} className="fc-img" alt="employee" />

            <div
              className={`fc-status-badge ${hasAlert ? "hasalert" : status}`}
            >
              <i className={`bi ${statusIcon}`} />
            </div>
          </div>

          <div className="fc-meta-details">
            <span className="fc-meta-rego">{vehicleCode}</span>
            <span className="fc-meta-name">{employeeName}</span>
          </div>

          <div className="fc-switch-wrap" onClick={(e) => e.stopPropagation()}>
            <label className="fc-switch" title="Track Vehicle">
              <input
                type="checkbox"
                checked={isTracking}
                onChange={() => onTrack(fleet.vehicleId)}
                aria-label={`Track ${vehicleCode}`}
              />
              <span className="fc-slider" />
            </label>
          </div>
        </div>

        {source === "haulweigh" && (
          <div className="fleet-card-content">
            {/* TRUCK BOX WITH HOVER BREAKDOWN */}
            <div className="fc-card-box fc-hover-expand">
              <div className="fc-weight-view">
                <label>TRUCK</label>
                <div className="fc-card-body-weight">
                  <span className="value highlight">
                    {formatWeight(fleet.truckWeight)}
                  </span>
                  <span className="fc-body-pct">
                    {bodyLoadPct.truck.toFixed(0)}%
                  </span>
                </div>
                <div className="fd-axle-bar">
                  <div
                    className={`fd-axle-bar-fill ${bodyLoadPct.truck > 90 ? "critical" : ""}`}
                    style={{ width: `${Math.min(bodyLoadPct.truck, 100)}%` }}
                  />
                </div>
              </div>

              {/* Dynamic Breakdown revealed on Hover */}
              <div className="fc-weight-breakdown">
                <div className="breakdown-item">
                  <span className="bd-label">STEER</span>
                  <span className="bd-value">
                    {formatWeight(fleet.weights?.steer || 0)}
                  </span>
                </div>
                <div className="breakdown-item">
                  <span className="bd-label">DRIVE</span>
                  <span className="bd-value">
                    {formatWeight(fleet.weights?.drive || 0)}
                  </span>
                </div>
              </div>
            </div>

            <div className="fc-card-box fc-hover-expand">
              <div className="fc-weight-view">
                <label>TRAILER</label>
                <div className="fc-card-body-weight">
                  <span className="value highlight">
                    {formatWeight(fleet.trailerWeight)}
                  </span>

                  <span className="fc-body-pct">
                    {bodyLoadPct.trailer.toFixed(0)}%
                  </span>
                </div>
                <div className="fd-axle-bar">
                  <div
                    className={`fd-axle-bar-fill ${
                      bodyLoadPct.trailer > 90 ? "critical" : ""
                    }`}
                    style={{ width: `${Math.min(bodyLoadPct.trailer, 100)}%` }}
                  />
                </div>
              </div>

              {/* Dynamic Breakdown revealed on Hover */}
              <div className="fc-weight-breakdown">
                <div className="breakdown-item">
                  <span className="bd-label">TRAILER A</span>
                  <span className="bd-value">
                    {formatWeight(fleet.weights?.trailerA || 0)}
                  </span>
                </div>
                <div className="breakdown-item">
                  <span className="bd-label">TRAILER B</span>
                  <span className="bd-value">
                    {formatWeight(fleet.weights?.trailerB || 0)}
                  </span>
                </div>
              </div>
            </div>
            <div className="fc-card-box full">
              <label>INDICATOR</label>
              <FleetCardStatus
                telemetry={{
                  speed: speedKmh,
                  binActive: fleet.flags?.binActive,
                  ptoActive: fleet.flags?.ptoActive,
                  tailGateActive: fleet.flags?.tailGateActive,
                  tarpActive: fleet.flags?.tarpActive,
                }}
              />
            </div>
            {/* <div className="fc-card-box">
            <label>REMARKS</label>
            <span>-</span>
          </div> */}
          </div>
        )}

        <div className="fc-card-address">
          <div className="fc-speedometer">
            <span className={`fc-speed ${speedKmh > 0 ? "moving" : ""}`}>
              {Math.round(speedKmh)}
            </span>
            <span className="fc-unit">KM/H</span>
          </div>
          <span className="fc-address">{fleet.address || ""}</span>
        </div>

        {fleet.subscriptionStatus && !hasValidSubscription && (
          <button
            type="button"
            className="fleet-btn fleet-btn-primary"
            onClick={(e) => {
              e.stopPropagation();
              navigate("/fleetmanager");
            }}
          >
            <span>Subscription required!</span>
          </button>
        )}
      </div>
    );
  },
  (prev, next) => {
    //todo ujj lesser the quicker but if you risk of data change miss
    const prevF = prev.fleet;
    const nextF = next.fleet;

    if (!prevF || !nextF) return false;

    //check server timestamp change
    if (prevF.serverTimestamp !== nextF.serverTimestamp) return false;

    // status change
    if (prevF.status !== nextF.status) return false;
    // speed change (affects UI)
    if (prevF.speedKmh !== nextF.speedKmh) return false;
    // weight affects UI
    if (prevF.totalWeight !== nextF.totalWeight) return false;

    // 🔴 for details update
    if (prevF.vehicleCode !== nextF.vehicleCode) return false;
    if (prevF.employee?._id !== nextF.employee?._id) return false;

    // selection change
    if (prev.isSelected !== next.isSelected) return false;
    //update ui on tracking state change todo ujj
    if (prev.isTracking !== next.isTracking) return false;

    // telemetry flags affect UI
    if (
      prevF.flags?.binActive !== nextF.flags?.binActive ||
      prevF.flags?.ptoActive !== nextF.flags?.ptoActive ||
      prevF.flags?.tailGateActive !== nextF.flags?.tailGateActive ||
      prevF.flags?.tarpActive !== nextF.flags?.tarpActive
    ) {
      return false;
    }

    return true;
  },
);

export default FleetCard;
